/**
 * The consent store (SC-2, DC-4): a module-level external store, read through
 * useSyncExternalStore. It resolves synchronously from the cookie on the first client read
 * (NFR-PERF-1) and is the single place a decision is written.
 */
import { COOKIE_NOTICE_VERSION, readStoredConsent, writeStoredConsent } from './cookie.js';
import { isGpcEnabled } from './gpc.js';
const SERVER_SNAPSHOT = Object.freeze({
    status: 'unknown',
    effective: 'denied',
    stored: null,
    gpc: false,
    bannerVisible: false,
    preferencesOpen: false,
});
let snapshot = null;
let recorder = null;
const listeners = new Set();
/** BR-2: the valid stored decision, else denied under GPC, else granted (opt-out default). */
export function resolveEffectiveConsent(stored, gpc) {
    if (stored)
        return stored.analytics;
    return gpc ? 'denied' : 'granted';
}
function build(stored, gpc, preferencesOpen) {
    return {
        status: 'resolved',
        effective: resolveEffectiveConsent(stored, gpc),
        stored,
        gpc,
        bannerVisible: stored === null && !gpc,
        preferencesOpen,
    };
}
function isClient() {
    return typeof document !== 'undefined';
}
function current() {
    if (!isClient())
        return null;
    if (!snapshot)
        snapshot = build(readStoredConsent(), isGpcEnabled(), false);
    return snapshot;
}
function update(next) {
    snapshot = next;
    for (const listener of listeners)
        listener();
}
export function getConsentSnapshot() {
    return current() ?? SERVER_SNAPSHOT;
}
/** The snapshot for server render and hydration: unresolved, no banner (NFR-PERF-1). */
export function getServerConsentSnapshot() {
    return SERVER_SNAPSHOT;
}
export function subscribeConsent(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
/**
 * Store an explicit decision: writes the cookie and notifies subscribers (trackers react at once,
 * BR-8). When a signed-in recorder is registered and `upload` is not false, the decision is also
 * sent to the server without awaiting it; a failure never reaches the UI (BR-13).
 */
export function setAnalyticsConsent(decision, source, opts = {}) {
    const state = current();
    if (!state)
        return;
    const stored = {
        v: 1,
        analytics: decision,
        noticeVersion: COOKIE_NOTICE_VERSION,
        decidedAt: opts.decidedAt ?? new Date().toISOString(),
    };
    writeStoredConsent(stored);
    update(build(stored, state.gpc, state.preferencesOpen));
    if (opts.upload !== false && recorder) {
        try {
            recorder({ ...stored, source }).catch(() => {
                // Best-effort upload (BR-13, NFR-OBSERVABILITY-1): no console output; the next
                // sign-in reconciliation retries it.
            });
        }
        catch {
            // Same best-effort contract for a recorder that throws synchronously.
        }
    }
}
export function openCookiePreferences() {
    const state = current();
    if (state && !state.preferencesOpen)
        update({ ...state, preferencesOpen: true });
}
export function closeCookiePreferences() {
    const state = current();
    if (state?.preferencesOpen)
        update({ ...state, preferencesOpen: false });
}
/** Register (or, with null, clear) the signed-in user's server recorder. */
export function registerConsentRecorder(fn) {
    recorder = fn;
}
/** True only when consent is resolved and the effective decision is granted. */
export function isAnalyticsGranted() {
    const state = getConsentSnapshot();
    return state.status === 'resolved' && state.effective === 'granted';
}
//# sourceMappingURL=store.js.map